(function () {
  'use strict';

  // Scroll reveals progressively enhance content that is visible by default.
  // A missing/blocked script or a failed browser feature never conceals it.
  function motionQuery(query, fallback) {
    try { return window.matchMedia(query); }
    catch (error) { return { matches: fallback }; }
  }

  var reduceMotion = motionQuery('(prefers-reduced-motion: reduce)', true);
  var precisePointer = motionQuery('(hover: hover) and (pointer: fine)', false);

  function activePanel(root) {
    return root.querySelector('[data-flux-panel]:not([hidden])');
  }

  function updateRailControls(root) {
    var panel = activePanel(root);
    var rail = panel && panel.querySelector('[data-flux-rail]');
    var previous = root.querySelector('[data-flux-rail-prev]');
    var next = root.querySelector('[data-flux-rail-next]');
    if (!rail || !previous || !next) return;

    var maximum = Math.max(0, rail.scrollWidth - rail.clientWidth);
    previous.disabled = rail.scrollLeft <= 4;
    next.disabled = rail.scrollLeft >= maximum - 4;
  }

  function moveRail(root, direction) {
    var panel = activePanel(root);
    var rail = panel && panel.querySelector('[data-flux-rail]');
    if (!rail) return;
    var card = rail.querySelector('.flux-ai-product');
    var distance = card ? card.getBoundingClientRect().width + 24 : rail.clientWidth * 0.8;
    rail.scrollBy({
      left: direction * distance,
      behavior: reduceMotion.matches ? 'auto' : 'smooth'
    });
  }

  function switchPanel(root, button) {
    var key = button.getAttribute('data-flux-filter');
    var nextPanel = root.querySelector('[data-flux-panel="' + key + '"]');
    var currentPanel = activePanel(root);
    if (!nextPanel || nextPanel === currentPanel) return;

    root.querySelectorAll('[data-flux-filter]').forEach(function (item) {
      var selected = item === button;
      item.classList.toggle('is-active', selected);
      item.setAttribute('aria-selected', selected ? 'true' : 'false');
      item.setAttribute('tabindex', selected ? '0' : '-1');
    });

    var generation = Number(root.dataset.fluxSwitchGeneration || 0) + 1;
    root.dataset.fluxSwitchGeneration = String(generation);

    function showNext() {
      if (Number(root.dataset.fluxSwitchGeneration) !== generation) return;
      if (currentPanel) {
        currentPanel.hidden = true;
        currentPanel.dataset.state = 'idle';
      }
      nextPanel.hidden = false;
      nextPanel.dataset.state = 'active';

      if (!reduceMotion.matches && nextPanel.animate) {
        nextPanel.animate(
          [
            { opacity: 0, transform: 'translateY(12px)', filter: 'blur(4px)' },
            { opacity: 1, transform: 'translateY(0)', filter: 'blur(0)' }
          ],
          { duration: 300, easing: 'cubic-bezier(.2,0,0,1)' }
        );
      }

      var status = root.querySelector('[data-flux-filter-status]');
      if (status) status.textContent = button.textContent.trim() + ' products selected.';
      requestAnimationFrame(function () { updateRailControls(root); });
    }

    showNext();
  }

  function initialiseBrowser(root) {
    if (root.dataset.fluxReady === 'true') return;
    root.dataset.fluxReady = 'true';

    var tabs = Array.from(root.querySelectorAll('[data-flux-filter]'));
    tabs.forEach(function (button, index) {
      button.addEventListener('click', function () { switchPanel(root, button); });
      button.addEventListener('keydown', function (event) {
        if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
        event.preventDefault();
        var nextIndex = event.key === 'ArrowRight'
          ? (index + 1) % tabs.length
          : (index - 1 + tabs.length) % tabs.length;
        tabs[nextIndex].focus();
        switchPanel(root, tabs[nextIndex]);
      });
    });

    var previous = root.querySelector('[data-flux-rail-prev]');
    var next = root.querySelector('[data-flux-rail-next]');
    if (previous) previous.addEventListener('click', function () { moveRail(root, -1); });
    if (next) next.addEventListener('click', function () { moveRail(root, 1); });

    root.querySelectorAll('[data-flux-rail]').forEach(function (rail) {
      var scheduled = false;
      rail.addEventListener('scroll', function () {
        if (scheduled) return;
        scheduled = true;
        requestAnimationFrame(function () {
          scheduled = false;
          updateRailControls(root);
        });
      }, { passive: true });
    });

    updateRailControls(root);
  }

  function initialiseTilt(scope) {
    if (reduceMotion.matches || !precisePointer.matches) return;
    scope.querySelectorAll('[data-flux-tilt]').forEach(function (card) {
      if (card.dataset.fluxTiltReady === 'true') return;
      card.dataset.fluxTiltReady = 'true';
      var frame = 0;
      var pointerX = 50;
      var pointerY = 50;

      card.addEventListener('pointermove', function (event) {
        var bounds = card.getBoundingClientRect();
        pointerX = ((event.clientX - bounds.left) / bounds.width) * 100;
        pointerY = ((event.clientY - bounds.top) / bounds.height) * 100;
        if (frame) return;
        frame = requestAnimationFrame(function () {
          frame = 0;
          card.style.setProperty('--spot-x', pointerX.toFixed(1) + '%');
          card.style.setProperty('--spot-y', pointerY.toFixed(1) + '%');
          card.style.setProperty('--tilt-x', ((pointerY - 50) * -0.055).toFixed(2) + 'deg');
          card.style.setProperty('--tilt-y', ((pointerX - 50) * 0.055).toFixed(2) + 'deg');
        });
      });

      card.addEventListener('pointerleave', function () {
        card.style.setProperty('--tilt-x', '0deg');
        card.style.setProperty('--tilt-y', '0deg');
        card.style.setProperty('--spot-x', '50%');
        card.style.setProperty('--spot-y', '50%');
      });
    });
  }

  var revealObserver = null;
  var revealPreferenceObserver = null;
  var revealPending = new Set();
  var revealFailed = false;
  var revealListenersReady = false;
  var revealSelector = '[data-flux-reveal]';
  var originalHomeTargets = [
    '.flx-cat-head', '.flx-col-title',
    '.flx-card-img', '.flx-card-info', '.flx-card-price',
    '.flx-drop__head', '.flx-drop__card',
    '.flxt__head', '.flxt__cta',
    '.flux-st__eyebrow', '.flux-st__title', '.flux-st__copy', '.flux-st__row',
    '.flux-ai-endcap__copy'
  ].join(',');

  function manualMotionOff() {
    return document.documentElement.dataset.fluxMotion === 'off' ||
      /(?:^\?|&)motion=off(?:&|$)/.test(window.location.search || '');
  }

  function revealMotionOff() {
    return reduceMotion.matches || manualMotionOff() ||
      document.documentElement.classList.contains('shopify-design-mode') ||
      (document.body && document.body.classList.contains('shopify-design-mode'));
  }

  function scopedItems(scope, selector) {
    var items = Array.from(scope.querySelectorAll(selector));
    if (scope.nodeType === 1 && scope.matches(selector)) items.unshift(scope);
    return items;
  }

  function settleReveal(item, instant) {
    if (instant) item.dataset.fluxRevealInstant = 'true';
    item.dataset.fluxRevealReady = 'true';
    item.dataset.fluxRevealState = instant ? 'settled' : 'revealed';
    item.dataset.fluxVisible = 'true';
    if (!instant) {
      var finish = function (event) {
        if (event.target !== item || event.propertyName !== 'opacity') return;
        if (item.dataset.fluxRevealState === 'revealed') item.dataset.fluxRevealState = 'settled';
        item.removeEventListener('transitionend', finish);
      };
      item.addEventListener('transitionend', finish);
    }
    var wasPending = revealPending.delete(item);
    if (wasPending && revealObserver) {
      try { revealObserver.unobserve(item); }
      catch (error) { failReveals(); }
    }
  }

  function settlePendingReveals() {
    Array.from(revealPending).forEach(function (item) { settleReveal(item, true); });
  }

  function disconnectReveals() {
    var observer = revealObserver;
    revealObserver = null;
    if (observer) {
      try { observer.disconnect(); } catch (error) { /* Content is already settled. */ }
    }
  }

  function failReveals() {
    revealFailed = true;
    disconnectReveals();
    settlePendingReveals();
  }

  function ensureRevealObserver() {
    if (revealObserver) return true;
    if (revealFailed || typeof window.IntersectionObserver !== 'function') return false;
    try {
      revealObserver = new window.IntersectionObserver(function (entries) {
        try {
          entries.forEach(function (entry) {
            if (entry.isIntersecting && revealPending.has(entry.target)) {
              settleReveal(entry.target, revealMotionOff());
            }
          });
        } catch (error) { failReveals(); }
      }, { threshold: 0, rootMargin: '0px' });
      return true;
    } catch (error) {
      failReveals();
      return false;
    }
  }

  function originalRevealItems(scope) {
    var home = document.querySelector('#MainContent[data-template="index"]');
    if (home) {
      scopedItems(scope, originalHomeTargets).forEach(function (item) {
        if (!home.contains(item)) return;
        // The existing hero, sticky orbit, video, rails and tilt parents are
        // not targets. Only already-present non-tilt semantic descendants enter.
        if (item.closest('.flx-hero, .flx-play, .flx-faction, [hidden], [aria-hidden="true"]')) return;
        var parentTarget = item.parentElement && item.parentElement.closest(revealSelector);
        if (parentTarget || item.querySelector(revealSelector)) return;
        item.setAttribute('data-flux-reveal', '');
      });
    }
    return scopedItems(scope, revealSelector);
  }

  function revealPath(target, descendants) {
    if (!target || target.nodeType !== 1) return;
    var node = target;
    while (node) {
      if (node.matches(revealSelector)) settleReveal(node, true);
      node = node.parentElement;
    }
    if (descendants) scopedItems(target, revealSelector).forEach(function (item) {
      settleReveal(item, true);
    });
  }

  function revealHash(hash) {
    if (!hash || hash === '#') return;
    try { revealPath(document.getElementById(decodeURIComponent(hash.slice(1))), true); }
    catch (error) { /* An invalid fragment does not affect page visibility. */ }
  }

  function watchRevealPreference() {
    if (revealPreferenceObserver || typeof window.MutationObserver !== 'function') return;
    try {
      revealPreferenceObserver = new window.MutationObserver(function () {
        if (manualMotionOff()) {
          settlePendingReveals();
          disconnectReveals();
        }
      });
      revealPreferenceObserver.observe(document.documentElement, {
        attributes: true, attributeFilter: ['data-flux-motion']
      });
    } catch (error) {
      if (revealPreferenceObserver) revealPreferenceObserver.disconnect();
      revealPreferenceObserver = null;
      // CSS still makes the manual-off state visible without this listener.
    }
  }

  function bindRevealListeners() {
    if (revealListenersReady) return;
    revealListenersReady = true;
    document.addEventListener('focusin', function (event) { revealPath(event.target, true); }, true);
    document.addEventListener('click', function (event) {
      var anchor = event.target.closest && event.target.closest('a[href]');
      if (!anchor) return;
      try {
        var url = new URL(anchor.getAttribute('href'), document.baseURI);
        if (url.origin === window.location.origin && url.pathname === window.location.pathname) {
          revealHash(url.hash);
        }
      } catch (error) { /* Ordinary link behaviour is unchanged. */ }
    }, true);
    window.addEventListener('hashchange', function () { revealHash(window.location.hash); });
    var preferenceChanged = function () {
      if (reduceMotion.matches) {
        settlePendingReveals();
        disconnectReveals();
      }
    };
    if (reduceMotion.addEventListener) reduceMotion.addEventListener('change', preferenceChanged);
    else if (reduceMotion.addListener) reduceMotion.addListener(preferenceChanged);
    document.addEventListener('shopify:section:unload', function (event) {
      Array.from(revealPending).forEach(function (item) {
        if (event.target === item || event.target.contains(item)) settleReveal(item, true);
      });
      if (!revealPending.size) disconnectReveals();
    });
    window.addEventListener('pagehide', function () {
      settlePendingReveals();
      disconnectReveals();
      if (revealPreferenceObserver) revealPreferenceObserver.disconnect();
      revealPreferenceObserver = null;
    });
    window.addEventListener('pageshow', function () {
      watchRevealPreference();
      if (revealMotionOff()) settlePendingReveals();
      revealHash(window.location.hash);
    });
    watchRevealPreference();
  }

  function revealDelay(item, groups) {
    var card = item.closest('.flx-card');
    if (card && card.parentElement) {
      // Image, name and price inside one existing tilt card share one delay;
      // the tilt parent and its hover transform are never modified.
      var cards = Array.from(card.parentElement.querySelectorAll('.flx-card'));
      return Math.max(0, cards.indexOf(card)) % 3 * 90;
    }
    var group = item.closest('.flx-cards, .flx-drop__grid, .flux-st__list, .flux-ai-sides__grid') ||
      item.closest('.flx-catalog, .flx-drop, .flxt, .flux-st, .flux-ai-field, .flux-ai-endcap') ||
      item.parentElement;
    var index = groups.get(group) || 0;
    groups.set(group, index + 1);
    return index % 3 * 90;
  }

  function initialiseReveals(scope) {
    var items = originalRevealItems(scope);
    if (!items.length) return;
    bindRevealListeners();
    var groups = new Map();
    items.forEach(function (item) {
      if (item.dataset.fluxRevealReady === 'true') return;
      var rect = item.getBoundingClientRect();
      var height = window.innerHeight || document.documentElement.clientHeight;
      var active = document.activeElement;
      var focused = active && active.nodeType === 1 && active !== document.body &&
        active !== document.documentElement && (item.contains(active) || active.contains(item));
      var hidden = item.closest('[hidden], [aria-hidden="true"]');
      var hero = item.closest('.flx-hero, .flx-play, .flx-faction');
      // Content already at/above the first screen, focused, or intentionally
      // hidden by another component is never put into a concealed state.
      if (revealMotionOff() || revealFailed || hero || hidden || focused ||
          item.dataset.fluxVisible === 'true' || !(height > 0) ||
          !item.getClientRects().length || rect.top < height || !ensureRevealObserver()) {
        settleReveal(item, true);
        return;
      }
      item.style.setProperty('--reveal-delay', revealDelay(item, groups) + 'ms');
      item.dataset.fluxRevealReady = 'true';
      item.dataset.fluxRevealState = 'prepared';
      revealPending.add(item);
      try {
        revealObserver.observe(item);
        // A successful observer owns this item before CSS may conceal it.
        // Retain a synchronous test observer's already-revealed state as well.
        if (item.dataset.fluxRevealState === 'prepared') item.dataset.fluxRevealState = 'pending';
      } catch (error) { failReveals(); }
    });
    revealHash(window.location.hash);
  }

  function initialise(scope) {
    var root = scope || document;
    try { initialiseReveals(root); }
    catch (error) { failReveals(); }
    document.documentElement.dataset.fluxInteractions = 'ready';
    root.querySelectorAll('[data-flux-product-browser]').forEach(initialiseBrowser);
    initialiseTilt(root);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { initialise(document); });
  } else {
    initialise(document);
  }

  document.addEventListener('shopify:section:load', function (event) {
    initialise(event.target);
  });

  window.addEventListener('resize', function () {
    document.querySelectorAll('[data-flux-product-browser]').forEach(updateRailControls);
  }, { passive: true });
})();
